import type { ButtonHTMLAttributes, Ref } from 'react';
import { FOCUS_RING } from './focusRing';

/**
 * The one button in the product.
 *
 * Before this existed the editor wrote its buttons inline, forty-three times, in
 * eleven radii and six paddings — and, more importantly, filled them with
 * `--ed-accent`. That conflated the two things the design language keeps apart:
 * the accent marks the CURRENT POSITION (the eyebrow, the active step dot), and
 * the primary action is near-black ink. When one colour does both, the accent
 * stops meaning anything, which is design-language.md §1 rule 3.
 *
 * The ladder, and the whole of it:
 *
 *   primary    ink fill, one per view — the thing that moves the flow forward
 *   secondary  same pill, no fill, ink label — a real action, just not THE one
 *   ghost      no border, dim label — dismissive ("Back", "Cancel")
 *   danger     destructive confirmations only
 *
 * Secondary is deliberately the primary with the fill removed rather than a
 * differently-shaped object: same radius, same height, same padding. Two
 * shapes read as two unrelated species of control no matter how the colour is
 * tuned, and that shape mismatch — not the colour — is what made the guided
 * flow look like four products stitched together.
 *
 * No variant here spends a hue, and that is the point. SELECTION IS NOT AN
 * ACTION: picking a roof shape or a unit system goes through `Segmented`,
 * which carries its selected state as depth (a raised thumb in a recessed
 * track) rather than colour. That leaves the accent free to mean exactly one
 * thing — where you are in the flow — and leaves the blue where the palette
 * doc puts it, on the acoustic data.
 *
 * `buttonClass()` rather than a polymorphic component: half these buttons are
 * `<Link>`s or the analytics-wrapped `<LaunchCta>`, and threading an `as` prop
 * through those buys nothing a class string does not already give.
 *
 * Every non-primary variant names its colour twice — `var(--ed-x,var(--rt-y))`.
 * The `--ed-*` half is the editor alias; the fallback is the very token that
 * alias points at in globals.css. It is there because `--ed-*` is defined on
 * `.guided-theme`, not `:root`, so the ladder used to paint an unstyled border
 * and inherited text anywhere outside the editor — the app-shell headers, the
 * marketing pages. A fallback rather than a second palette: the two halves are
 * the same colour by construction, and `buttonLadderTokens.test.ts` reads both
 * out of the source and fails if they ever stop being.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonClassOptions {
  variant?: ButtonVariant;
  /** `sm` is the dense editor row (32px); `md` clears the app's 44px tap floor. */
  size?: ButtonSize;
  className?: string;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--rt-button)] text-[var(--rt-button-label)] hover:bg-[var(--rt-button-hover)]',
  // Ink label, not `--ed-text-dim`. A bordered button whose label is already
  // dimmed at rest reads as disabled, which is why the tier it replaced kept
  // getting used for Cancel — those moved to `ghost`, where they belonged.
  secondary:
    'border border-[var(--ed-border,var(--rt-hairline))] text-[var(--ed-text,var(--rt-ink))] ' +
    'hover:border-[var(--ed-border-bright,var(--rt-hairline-strong))] hover:bg-[var(--ed-surface-2,var(--rt-hover))]',
  ghost: 'text-[var(--ed-text-dim,var(--rt-ink-dim))] hover:text-[var(--ed-text,var(--rt-ink))]',
  // Destructive confirmations only. `status.danger` reports an outcome, so it is
  // sanctioned here in the same way it is sanctioned on an error message.
  danger:
    'border border-[var(--ed-border,var(--rt-hairline))] text-[var(--ed-text-dim,var(--rt-ink-dim))] '
    + 'hover:border-[var(--ed-red,var(--rt-danger))] hover:text-[var(--ed-red,var(--rt-danger))]',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-3.5 text-body-sm',
  // `--tap-target` rather than a literal h-11: inside the editor's `--ui-zoom`
  // subtree a 44px button is only 35px under a thumb, and outside it the
  // variable is just 44. See globals.css.
  md: 'min-h-[var(--tap-target)] gap-2 px-5 text-body-sm',
};

const BASE =
  'inline-flex shrink-0 items-center justify-center rounded-full font-semibold ' +
  `transition-colors active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-60 disabled:active:scale-100 ${FOCUS_RING}`;

export function buttonClass({
  variant = 'primary',
  size = 'sm',
  className = '',
}: ButtonClassOptions = {}): string {
  return [BASE, SIZES[size], VARIANTS[variant], className].filter(Boolean).join(' ');
}

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>,
    ButtonClassOptions {
  /** React 19: a plain prop, passed through to the <button> (a menu anchors
   *  itself to its trigger through it). */
  ref?: Ref<HTMLButtonElement>;
}

export default function Button({ variant, size, className, type, ...rest }: ButtonProps) {
  return (
    <button
      // Every one of the inline buttons this replaces wrote `type="button"`, and
      // the one that forgot submitted a form on click. Defaulting it here means
      // that bug cannot come back.
      type={type ?? 'button'}
      data-cta={variant === 'primary' || variant === undefined ? 'primary' : undefined}
      className={buttonClass({ variant, size, className })}
      {...rest}
    />
  );
}
